/**
 * Solana Service
 *
 * Singleton providing a shared @solana/kit RPC client and common operations.
 * All RPC calls go through the backend proxy at /api/rpc for rate limiting,
 * caching, and auth. The proxy accepts Bearer JWT tokens which we inject via
 * a custom Kit transport. Signed writes use the controlled relay and durable
 * operation journal in transaction-relay.ts; the RPC proxy stays read-only.
 */

import {
  address as toAddress,
  appendTransactionMessageInstruction,
  compileTransaction,
  createSolanaRpcFromTransport,
  createTransactionMessage,
  setTransactionMessageFeePayer,
  setTransactionMessageLifetimeUsingBlockhash,
  type Address,
  type Rpc,
  type RpcTransport,
  type SolanaRpcApi,
} from "@solana/kit";
import { getTransferSolInstruction } from "@solana-program/system";
import {
  getSolanaRpcEndpoint,
  LAMPORTS_PER_SOL,
  API_TIMEOUT,
} from "@/config/constants";
import { useAuthStore } from "@/store/auth-store";
import type { TransactionResult } from "@/types/solana";
import { transactionRelay, decimalToBaseUnits, type SignTransactionFn } from "./transaction-relay";

/**
 * Build a Kit `RpcTransport` that injects the current JWT from the auth store
 * (if any) into every JSON-RPC POST. Falls back to an unauthenticated request
 * when no token is present — the backend proxy allows read-only calls without
 * auth.
 */
function createAuthenticatedTransport(url: string): RpcTransport {
  const transport: RpcTransport = async <TResponse>({
    payload,
    signal,
  }: Parameters<RpcTransport>[0]): Promise<TResponse> => {
    const token = useAuthStore.getState().token;
    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify(payload),
      signal,
    });
    if (!response.ok) {
      throw new Error(`RPC HTTP ${response.status}: ${await response.text()}`);
    }
    return (await response.json()) as TResponse;
  };
  return transport;
}

class SolanaService {
  private rpc: Rpc<SolanaRpcApi> | null = null;

  /** Shared Kit RPC (lazy-initialized). */
  getRpc(): Rpc<SolanaRpcApi> {
    if (!this.rpc) {
      const transport = createAuthenticatedTransport(getSolanaRpcEndpoint());
      this.rpc = createSolanaRpcFromTransport(transport);
    }
    return this.rpc;
  }

  /**
   * Get SOL balance for an address.
   * @returns Balance in SOL (not lamports).
   */
  async getBalance(wallet: Address): Promise<number> {
    const { value: lamports } = await this.getRpc()
      .getBalance(wallet)
      .send();
    return Number(lamports) / LAMPORTS_PER_SOL;
  }

  /**
   * Send SOL via a Kit transaction, signed by the wallet's signTransaction
   * callback. Persists the signed operation before controlled relay submission.
   */
  async sendSOL(
    from: Address,
    to: Address,
    amount: number | string,
    signTransaction: SignTransactionFn,
  ): Promise<TransactionResult> {
    return transactionRelay.execute(
      from,
      { recipient: to, amount: String(amount), asset: "SOL" },
      async () => {
        const { value: blockhashInfo } = await this.getRpc().getLatestBlockhash().send({ abortSignal: AbortSignal.timeout(API_TIMEOUT) });
        const ix = getTransferSolInstruction({
          source: { address: from, signTransactions: async () => [] } as never,
          destination: to,
          amount: decimalToBaseUnits(amount, 9),
        });
        const message = appendTransactionMessageInstruction(ix,
          setTransactionMessageLifetimeUsingBlockhash(blockhashInfo,
            setTransactionMessageFeePayer(from, createTransactionMessage({ version: 0 }))));
        return compileTransaction(message);
      },
      signTransaction,
      this.getRpc(),
    );
  }
}

/** Singleton instance. */
export const solanaService = new SolanaService();
export { toAddress };
